import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://iwclbgjdcglabmaxtuqw.supabase.co';
const SUPABASE_ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml3Y2xiZ2pkY2dsYWJtYXh0dXF3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4MjQwNTcsImV4cCI6MjA4ODQwMDA1N30.U-N2gq0yrPKv7PsIfYj398WxJwHFx-XOA8HfJ3ZKBUc';

// Le client envoie le JWT PAR UTILISATEUR émis à la connexion (claims
// app_role / app_user_id vérifiés par les policies RLS). Sans jeton
// (formulaires publics, session pas encore rafraîchie), il retombe sur
// la clé anon : les tables protégées refusent alors l'accès.
function getUserJwt() {
  try { return localStorage.getItem('elixir_supa_jwt') || null; } catch { return null; }
}

// Supabase plafonne chaque requête à 1 000 lignes, quel que soit .limit().
// fetchAllRows lit par pages et renvoie tout (ou jusqu'à maxRows).
// Usage : const rows = await fetchAllRows(() => supabase.from('t').select('*').order('x'));
export async function fetchAllRows(makeQuery, { pageSize = 1000, maxRows = 50000 } = {}) {
  const out = [];
  for (let from = 0; from < maxRows; from += pageSize) {
    const { data, error } = await makeQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }
  return out;
}

// ⚠️ ROLLBACK TEMPORAIRE : le jeton par utilisateur est désactivé le temps
// de diagnostiquer son rejet par Supabase (app vide). Retour à la clé anon.
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// ── Intercepteur : aucune ÉCRITURE en base ne doit échouer en silence ──
// supabase-js ne lève pas d'exception : il renvoie { error }. Beaucoup
// d'appels historiques ne le lisent pas. On intercepte donc au niveau du
// client : toute erreur sur insert/update/upsert/delete est journalisée et
// diffusée (événement 'elixir:db-error') pour affichage d'un bandeau.
// Les violations d'unicité (23505) sont exclues : elles sont gérées
// volontairement par les appelants (anti-doublons, prise atomique).
export function reportDbError(op, table, error) {
  if (!error) return;
  if (error.code === '23505') return;
  console.error('[Supabase ' + op + ' ' + table + ']', error.message, error);
  try {
    window.dispatchEvent(new CustomEvent('elixir:db-error', {
      detail: { op, table, message: error.message || String(error), at: Date.now() },
    }));
  } catch {}
}

// ══════════════════════════════════════════════════════════════
// JOURNAL UNIVERSEL — qui a modifié quoi, partout dans l'app
//
// Audit du 10/10 : 122 livraisons réellement parties avaient été vidées de
// leurs caisses et de leurs photos, sans qu'aucune trace ne dise par qui.
// Plutôt que d'instrumenter les 217 endroits où l'app écrit, on se branche
// ici : toute écriture passe déjà par cet intercepteur.
//
// Trois précautions :
//  - les écritures du journal lui-même ne sont jamais journalisées (boucle) ;
//  - les lignes partent par paquets toutes les 4 s, pour ne pas multiplier les
//    requêtes ni peser sur le budget d'écriture disque ;
//  - un échec du journal n'interrompt jamais le travail en cours.
// ══════════════════════════════════════════════════════════════

const TABLES_NON_JOURNALISEES = new Set([
  'elixir_audit',            // soi-même : sinon boucle infinie
  'elixir_prep_journal',     // journal métier détaillé, déjà explicite
]);

let _utilisateur = { id: null, name: null };
/** Appelé à la connexion : le journal a besoin de savoir qui agit. */
export function setJournalUser(u) {
  _utilisateur = { id: u?.id ?? null, name: u?.name ?? null };
  try { localStorage.setItem('elixir_journal_user', JSON.stringify(_utilisateur)); } catch {}
}
(function restaurerUtilisateur() {
  try {
    const v = JSON.parse(localStorage.getItem('elixir_journal_user') || 'null');
    if (v && (v.id || v.name)) _utilisateur = v;
  } catch {}
})();

// Identifie l'appareil, pas la personne : sur un téléphone partagé, c'est ce
// qui permet de dire « deux noms différents depuis le même appareil ».
const _appareil = (() => {
  try {
    let id = localStorage.getItem('elixir_appareil_id');
    if (!id) {
      id = Math.random().toString(36).slice(2, 10);
      localStorage.setItem('elixir_appareil_id', id);
    }
    const ua = navigator.userAgent || '';
    const marque = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'Mac' : 'autre';
    return marque + '-' + id;
  } catch { return 'inconnu'; }
})();

let _tampon = [];
let _minuteur = null;

// Ne garder que l'essentiel : les champs touchés, pas leur contenu complet
// (une photo en base64 ou 500 lignes de commande n'ont rien à faire là).
function champsTouches(args) {
  try {
    const v = args && args[0];
    if (!v) return null;
    const un = Array.isArray(v) ? v[0] : v;
    if (!un || typeof un !== 'object') return null;
    const champs = Object.keys(un).filter(k => k !== 'updated_at').slice(0, 25);
    const extrait = {};
    for (const k of champs) {
      const val = un[k];
      if (val === null || ['number', 'boolean'].includes(typeof val)) extrait[k] = val;
      else if (typeof val === 'string') extrait[k] = val.length > 60 ? val.slice(0, 60) + '…' : val;
      else extrait[k] = Array.isArray(val) ? '[' + val.length + ']' : '{…}';
    }
    return { champs, valeurs: extrait, lignes: Array.isArray(v) ? v.length : 1 };
  } catch { return null; }
}

async function viderTampon() {
  _minuteur = null;
  if (!_tampon.length) return;
  const lot = _tampon.splice(0, 200);
  try {
    // _rawFrom : on court-circuite l'intercepteur pour ne pas se journaliser
    const { error } = await _rawFrom('elixir_audit').insert(lot);
    if (error) console.warn('[journal]', error.message);
  } catch (e) { console.warn('[journal]', e?.message || e); }
}

function journaliserEcriture(op, table, args, res) {
  if (TABLES_NON_JOURNALISEES.has(table)) return;
  try {
    const lignes = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
    _tampon.push({
      fait_a: new Date().toISOString(),   // heure du geste (≠ heure d'arrivée si hors ligne)
      appareil: _appareil,
      table_cible: table,
      operation: op,
      ligne_ids: lignes.slice(0, 20).map(r => String(r?.id ?? '')).filter(Boolean),
      nb_lignes: res?.count ?? lignes.length ?? null,
      detail: champsTouches(args),
      user_id: _utilisateur.id,
      user_name: _utilisateur.name,
    });
    if (_tampon.length >= 50) { clearTimeout(_minuteur); viderTampon(); }
    else if (!_minuteur) _minuteur = setTimeout(viderTampon, 4000);
  } catch {}
}

// Ne rien perdre si l'onglet se ferme ou passe en arrière-plan
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { try { viderTampon(); } catch {} });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { try { viderTampon(); } catch {} }
  });
}

// ── Fichiers : l'effacement d'une photo est une destruction de preuve ──
// supabase.storage ne passe pas par supabase.from : il faut l'intercepter aussi.
const _rawStorageFrom = supabase.storage.from.bind(supabase.storage);
supabase.storage.from = function (bucket) {
  const api = _rawStorageFrom(bucket);
  for (const op of ['upload', 'remove', 'move', 'copy', 'update']) {
    if (typeof api[op] !== 'function') continue;
    const orig = api[op].bind(api);
    api[op] = async function (...args) {
      const res = await orig(...args);
      try {
        const chemins = Array.isArray(args[0]) ? args[0] : [args[0]];
        _tampon.push({
          fait_a: new Date().toISOString(),
          appareil: _appareil,
          table_cible: 'fichier:' + bucket,
          operation: op,
          ligne_ids: chemins.filter(c => typeof c === 'string').slice(0, 20),
          nb_lignes: chemins.length,
          detail: { bucket, echec: res?.error ? (res.error.message || true) : null },
          user_id: _utilisateur.id,
          user_name: _utilisateur.name,
        });
        if (_tampon.length >= 50) { clearTimeout(_minuteur); viderTampon(); }
        else if (!_minuteur) _minuteur = setTimeout(viderTampon, 4000);
      } catch {}
      return res;
    };
  }
  return api;
};

// ── Fonctions base (rpc) : elles modifient aussi, et n'étaient pas vues ──
const _rawRpc = supabase.rpc.bind(supabase);
supabase.rpc = function (fn, params, options) {
  const q = _rawRpc(fn, params, options);
  try {
    const origThen = q.then.bind(q);
    q.then = function (onOk, onErr) {
      return origThen((res) => {
        try {
          if (!/^prep_stats_|^get_/.test(fn)) {   // les fonctions de lecture n'ont rien à faire là
            _tampon.push({
              fait_a: new Date().toISOString(),
              appareil: _appareil,
              table_cible: 'fonction:' + fn,
              operation: 'rpc',
              detail: { params: params || null, echec: res?.error ? (res.error.message || true) : null },
              user_id: _utilisateur.id,
              user_name: _utilisateur.name,
            });
            if (!_minuteur) _minuteur = setTimeout(viderTampon, 4000);
          }
        } catch {}
        return onOk ? onOk(res) : res;
      }, onErr);
    };
  } catch {}
  return q;
};

const _rawFrom = supabase.from.bind(supabase);
supabase.from = function (table) {
  const builder = _rawFrom(table);
  for (const op of ['insert', 'update', 'upsert', 'delete']) {
    if (typeof builder[op] !== 'function') continue;
    const orig = builder[op].bind(builder);
    builder[op] = function (...args) {
      const q = orig(...args);
      const origThen = q.then.bind(q);
      q.then = function (onOk, onErr) {
        return origThen((res) => {
          if (res && res.error) reportDbError(op, table, res.error);
          else journaliserEcriture(op, table, args, res);
          return onOk ? onOk(res) : res;
        }, onErr);
      };
      return q;
    };
  }
  return builder;
};
